import React from 'react';
import { Clock, Hourglass } from 'iconoir-react';
import { useMyInterests } from '../../hooks/useDiscover';

/**
 * Jun-19 (a user V1 test): "I expressed interest in someone on Discover, yet
 * nothing populates in the 'Waiting on them' tab."
 *
 * Root cause: a Discover "express interest" creates a one-directional
 * `connection_interests` row (status 'pending'); it only becomes a `matches`
 * row once the other side reciprocates. The Matches "Waiting on them" tab is
 * matches-only, and NOTHING in the web app rendered the user's SENT interests
 * (`useMyInterests` was only ever called with type='received', for "Interested
 * in you"). So an expressed interest was saved but invisible to the sender.
 *
 * This is the sender-side mirror of `ReceivedInterests`: it surfaces the user's
 * still-pending sent interests as "Waiting to hear back". It is read-only and
 * stays ANONYMOUS (names are revealed only on mutual interest, when the pair
 * shows up in Matches), consistent with the anonymous-until-mutual model.
 *
 * Renders nothing when there are no pending sent interests, so it never
 * clutters the page.
 */
const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  const weeks = Math.floor(diffDays / 7);
  if (diffDays < 30) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(diffDays / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
};

const SentInterests: React.FC = () => {
  const { data, isLoading } = useMyInterests({ type: 'sent', page: 1, limit: 10 });

  // Only still-pending sent interests belong here. Mutual ones have become a
  // match (shown in Matches); declined/expired are no longer "waiting".
  const interests = ((data?.result?.interests as any[]) || []).filter(
    i => i?.status === 'pending',
  );

  if (isLoading || interests.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-purple-25 p-4 mb-6">
      <h3 className="font-semibold text-heading-500 mb-1 flex items-center gap-2">
        <Hourglass className="w-5 h-5 text-primary-500" /> Waiting to hear back
        <span className="text-xs font-medium bg-primary-50 text-primary-600 px-2 py-0.5 rounded-full">
          {interests.length}
        </span>
      </h3>
      <p className="text-sm text-body-400 mb-3">
        You expressed interest in these members. We'll let you know if they connect back; names are revealed only when both sides do.
      </p>
      <div className="space-y-2">
        {interests.map(i => (
          <div
            key={i.id}
            className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg p-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-heading-600 truncate">
                Waiting to hear back from a member
              </p>
              {i.message && (
                <p className="text-xs text-body-400 truncate">"{i.message}"</p>
              )}
              {i.created_at && (
                <p className="text-[11px] text-body-300 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" /> Sent {getTimeAgo(i.created_at)}
                </p>
              )}
            </div>
            <span className="flex-shrink-0 text-xs font-medium bg-gray-100 text-body-500 px-2.5 py-1 rounded-full">
              Pending
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SentInterests;
